import React from 'react';
import { Card, Row, Col, Statistic, Spin, Alert, Button } from 'antd';
import {
    LockOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    SafetyOutlined,
    UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/common/ThemeProvider';
import { useGetBlockedUsers, useGetSecurityConfig } from '../hooks/useUserSecurity';

/**
 * Dashboard widget showing user security statistics
 * Can be embedded in main dashboard or user management page
 */
export const UserSecurityDashboard: React.FC = () => {
    const { dark } = useTheme();
    const navigate = useNavigate();
    const { data: blockedUsersResponse, isLoading, error } = useGetBlockedUsers();
    const { data: configResponse } = useGetSecurityConfig();


    if (isLoading) {
        return (
            <Card>
                <Spin tip="Loading security statistics..." />
            </Card>
        );
    }
    const blockedUsers = blockedUsersResponse;
    const config = configResponse;

    if (error) {
        return (
            <Card>
                <Alert
                    message="Error Loading Security Data"
                    description="Failed to load user security statistics"
                    type="error"
                    showIcon
                />
            </Card>
        );
    }
// @ts-ignore
    const adminBlocked = blockedUsers?.filter(u => u.blockedByAdmin).length || 0;
    // @ts-ignore
    const autoBlocked = blockedUsers?.filter(u => !u.blockedByAdmin).length || 0;
    // @ts-ignore
    const totalBlocked = blockedUsers?.length || 0;
    // @ts-ignore
    const totalAtRisk = blockedUsers?.filter(u =>
        (u.failedLoginAttempts || 0) >= 3 && !u.blocked
    ).length || 0;

    return (
        <Card
            title={
                <div className="flex items-center justify-between">
                    <span>
                        <SafetyOutlined className="mr-2" />
                        User Security Overview
                    </span>
                    <Button
                        size="small"
                        onClick={() => navigate('/admin/nub/user-security')}
                    >
                        View Details
                    </Button>
                </div>
            }
            className={dark ? 'bg-gray-800' : ''}
        >
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                    <Statistic
                        title="Total Blocked"
                        value={totalBlocked}
                        prefix={<LockOutlined />}
                        valueStyle={{ color: totalBlocked > 0 ? '#cf1322' : '#52c41a' }}
                        suffix={
                            config?.enabled && (
                                <span className="text-xs text-gray-500">
                                    / {config.maxFailedAttempts} max attempts
                                </span>
                            )
                        }
                    />
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Statistic
                        title="Admin Blocks"
                        value={adminBlocked}
                        prefix={<WarningOutlined />}
                        valueStyle={{ color: adminBlocked > 0 ? '#fa8c16' : '#52c41a' }}
                    />
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Statistic
                        title="Auto Blocks"
                        value={autoBlocked}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: autoBlocked > 0 ? '#faad14' : '#52c41a' }}
                        suffix={
                            config?.lockDurationHours && (
                                <span className="text-xs text-gray-500">
                                    / {config.lockDurationHours}h duration
                                </span>
                            )
                        }
                    />
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Statistic
                        title="At Risk"
                        value={totalAtRisk}
                        prefix={<UserOutlined />}
                        valueStyle={{ color: totalAtRisk > 0 ? '#ff7a45' : '#52c41a' }}
                        suffix={
                            <span className="text-xs text-gray-500">
                                3+ failed attempts
                            </span>
                        }
                    />
                </Col>
            </Row>

            {config && !config.enabled && (
                <Alert
                    message="Login Security Disabled"
                    description="Login attempt tracking is currently disabled in system configuration"
                    type="warning"
                    showIcon
                    className="mt-4"
                />
            )}

            {totalBlocked > 0 && (
                <div className="mt-4 text-sm text-gray-500">
                    <p>
                        {adminBlocked > 0 && `${adminBlocked} user${adminBlocked > 1 ? 's' : ''} blocked by administrators. `}
                        {autoBlocked > 0 && `${autoBlocked} user${autoBlocked > 1 ? 's' : ''} auto-blocked due to failed login attempts.`}
                    </p>
                </div>
            )}
        </Card>
    );
};
